import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Clock, Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { DIAS, ESCALAS, type Calendario } from './tipos';

/** Horas de cada dia da semana como barras proporcionais a 12 horas. */
function Semana({ c }: { c: Calendario }) {
  const { t } = useTranslation('pessoas');
  return (
    <div className="grid grid-cols-7 gap-1.5" role="img" aria-label={DIAS.map((d) => `${t(`dias.${d}`)} ${c.horas[d]}h`).join(', ')}>
      {DIAS.map((d) => (
        <div key={d} className="flex min-w-0 flex-col items-center gap-1">
          <div className="flex h-14 w-full items-end overflow-hidden rounded bg-sup-2">
            <div className="w-full bg-ciano" style={{ height: `${Math.min(100, (c.horas[d] / 12) * 100)}%` }} />
          </div>
          <span className="numero text-xs text-tinta-2">{numero(c.horas[d], c.horas[d] % 1 ? 2 : 0)}</span>
          <span className="text-xs text-tinta-3">{t(`dias.${d}`)}</span>
        </div>
      ))}
    </div>
  );
}

/** M7.4: calendários de trabalho com horas por dia da semana e escala (5x2, 6x1, 12x36). */
export function Jornada() {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Calendario | 'novo' | null>(null);
  const [remover, setRemover] = useState<Calendario | null>(null);
  const [horarios, setHorarios] = useState<Calendario | null>(null);
  // Os horários só valem para o Registro de ponto: com ele desligado no workspace (módulo liberado por workspace), nada aparece.
  const comPonto = s.instalado('ponto');
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  const q = useQuery({ queryKey: ['pessoas', 'calendarios'], queryFn: () => getTodos<Calendario>('/pessoas/calendarios') });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pessoas/calendarios/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await recarregar();
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'escala', rotulo: t('campos.escala'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: ESCALAS.map((e) => ({ valor: e, rotulo: t(`escala.${e}`) })) },
    ...DIAS.map((d): DefCampo => ({ nome: `h_${d}`, rotulo: t(`diasLongo.${d}`), tipo: 'numero', min: 0, max: 24, passo: 0.25, largura: 'terco' })),
  ];

  const inicial: Record<string, unknown> = atual
    ? { nome: atual.nome, escala: atual.escala, ...Object.fromEntries(DIAS.map((d) => [`h_${d}`, atual.horas[d]])) }
    : { escala: '5x2', ...Object.fromEntries(DIAS.map((d) => [`h_${d}`, d === 'sab' || d === 'dom' ? 0 : 8])) };

  return (
    <>
      {s.pode('pessoas.configurar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoCalendario')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={q} vazio={(d) => d.itens.length === 0}>
        {(d) => (
          <div className="grid min-w-0 gap-4 md:grid-cols-2">
            {d.itens.map((c) => (
              <Cartao
                key={c.id}
                titulo={c.nome}
                acao={
                  <span className="flex items-center gap-1">
                    {s.pode('pessoas.configurar') && (
                      <button type="button" aria-label={tn('comum.editar')} onClick={() => setAberto(c)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                        <Pencil className="size-4" />
                      </button>
                    )}
                    {comPonto && s.pode('pessoas.configurar') && (
                      <button type="button" aria-label={`${t('jornada.horarios')} · ${c.nome}`} onClick={() => setHorarios(c)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                        <Clock className="size-4" />
                      </button>
                    )}
                    {s.pode('pessoas.configurar') && (
                      <button type="button" aria-label={tn('comum.excluir')} onClick={() => setRemover(c)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </span>
                }
              >
                <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                  <Selo cor="ciano" quebra>
                    {t(`escala.${c.escala}`)}
                  </Selo>
                  <span className="numero text-tinta-2">{t('jornada.horasSemana', { n: numero(c.horas_semana, c.horas_semana % 1 ? 2 : 0) })}</span>
                  <span className="text-xs text-tinta-3">{t('jornada.funcionarios', { count: c.funcionarios })}</span>
                </div>
                <Semana c={c} />
                {comPonto && DIAS.some((d) => c.horarios?.[d]) && (
                  <ul className="mt-3 flex flex-col gap-0.5 text-xs text-tinta-3">
                    {DIAS.filter((d) => c.horarios?.[d]).map((d) => (
                      <li key={d} className="numero">
                        {t('jornada.horarioDia', { dia: t(`dias.${d}`), entrada: c.horarios![d]!.entrada, saida: c.horarios![d]!.saida })}
                      </li>
                    ))}
                  </ul>
                )}
              </Cartao>
            ))}
          </div>
        )}
      </Estado>
      <Modal
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? atual.nome : t('acoes.novoCalendario')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-calendario">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-3">{t('jornada.ajuda')}</p>
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-calendario"
          campos={campos}
          inicial={inicial}
          aoEnviar={async (v) => {
            const corpo = { nome: v.nome, escala: v.escala, horas: Object.fromEntries(DIAS.map((d) => [d, Number(v[`h_${d}`] ?? 0)])) };
            if (atual) await patch(`/pessoas/calendarios/${atual.id}`, corpo);
            else await post('/pessoas/calendarios', corpo);
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Modal>
      {horarios && <Horarios calendario={horarios} aoFechar={() => setHorarios(null)} aoSalvar={recarregar} />}
      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirCalendario')} texto={t('confirmar.excluirCalendarioTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}

/** Registro de ponto (lote 1): entrada, intervalo e saída de cada dia do calendário (dia vazio: valem só as horas). */
function Horarios({ calendario: c, aoFechar, aoSalvar }: { calendario: Calendario; aoFechar: () => void; aoSalvar: () => Promise<unknown> }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const campos: DefCampo[] = DIAS.flatMap((d): DefCampo[] => [
    { nome: `${d}_entrada`, rotulo: `${t(`diasLongo.${d}`)}: ${t('jornada.entrada')}`, tipo: 'hora', largura: 'meia' },
    { nome: `${d}_saida`, rotulo: `${t(`diasLongo.${d}`)}: ${t('jornada.saida')}`, tipo: 'hora', largura: 'meia' },
    { nome: `${d}_intervalo_inicio`, rotulo: `${t(`diasLongo.${d}`)}: ${t('jornada.intervaloInicio')}`, tipo: 'hora', largura: 'meia' },
    { nome: `${d}_intervalo_fim`, rotulo: `${t(`diasLongo.${d}`)}: ${t('jornada.intervaloFim')}`, tipo: 'hora', largura: 'meia' },
  ]);
  const inicial = Object.fromEntries(
    DIAS.flatMap((d) => {
      const h = c.horarios?.[d];
      return h ? [[`${d}_entrada`, h.entrada], [`${d}_saida`, h.saida], [`${d}_intervalo_inicio`, h.intervalo_inicio ?? ''], [`${d}_intervalo_fim`, h.intervalo_fim ?? '']] : [];
    }),
  );
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={`${t('jornada.horarios')} · ${c.nome}`}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-horarios">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <p className="mb-4 text-sm text-tinta-3">{t('jornada.horariosAjuda')}</p>
      <Formulario
        id="form-horarios"
        campos={campos}
        inicial={inicial}
        aoEnviar={async (v) => {
          const texto = (x: unknown) => (typeof x === 'string' && x ? x.slice(0, 5) : null);
          const horarios = Object.fromEntries(
            DIAS.map((d) => {
              const entrada = texto(v[`${d}_entrada`]);
              const saida = texto(v[`${d}_saida`]);
              return [d, entrada && saida ? { entrada, saida, intervalo_inicio: texto(v[`${d}_intervalo_inicio`]), intervalo_fim: texto(v[`${d}_intervalo_fim`]) } : null];
            }),
          );
          await patch(`/pessoas/calendarios/${c.id}`, { horarios });
          await aoSalvar();
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Modal>
  );
}
